<template>
  <layout-page :nav-bar-show-logo-text="false">
    <div class="mx-auto justify-center bg-repeat lg:bg-hero-tile">
      <div
        class="w-full bg-home bg-no-repeat lg:h-[538px] lg:border-b lg:border-solid lg:border-grey-20 lg:bg-hero-970 lg:pt-[100px] xl:h-[629px] xl:bg-hero-desktop xl:pt-[195px]"
      >
        <div class="container">
          <h1
            class="py-4 text-center text-2xl font-medium text-blue-60 lg:text-left lg:text-3xl"
          >
            The <span class="font-bold">fast</span> and
            <span class="font-bold">easy</span> way to find a recruitment
            agency.
          </h1>
          <p class="hidden text-grey-70 lg:mb-6 lg:block lg:pl-2 lg:text-xl">
            Helping
            <span class="font-bold text-green">{{ employerCount }}</span>
            employers and
            <span class="font-bold text-green">{{ candidateCount }}</span>
            candidates since 2000
          </p>
        </div>
        <div class="w-full lg:container lg:px-0">
          <search-bar
            page-type="home"
            :job_type="parameters.job_types && parameters.job_types[0]"
            :industry="parameters.industry"
            :location="currentLocationOption"
            :skill="parameters.skill"
            :skill-options="getSearchbarSkillOptions(parameters.industry)"
            :emp_cand="parameters.emp_cand"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <div class="w-full px-4 lg:container lg:mt-[-50px]">
        <popular-links
          :all-industries="getAllIndustryLinks"
          :popular-industries="getPopularIndustryLinks"
          :popular-locations="getPopularLocationLinks"
        />
      </div>
      <div class="w-full px-4 lg:container">
        <ac-accordion
          class="block lg:hidden"
          :entries="{
            about: 'About AgencyCentral',
            employers: 'Employers',
            candidates: 'Candidates',
            whyuse: 'Why use a Recruitment Agency?',
          }"
          :selected-key="selectedTabKey"
          @change="tabInfoChange"
        >
          <template #body-about>
            <about-agency-central-info />
          </template>
          <template #body-employers>
            <employer-info />
          </template>
          <template #body-candidates>
            <candidate-info />
          </template>
          <template #body-whyuse>
            <why-use-agency-info />
          </template>
        </ac-accordion>

        <tabbed-card
          class="hidden lg:block"
          :allow-toggle-close="false"
          :entries="{
            about: 'About AgencyCentral',
            employers: 'Employers',
            candidates: 'Candidates',
            whyuse: 'Why use a Recruitment Agency?',
          }"
          :selected-key="selectedTabKey"
          @change="tabInfoChange"
        >
          <template #body-about>
            <about-agency-central-info />
          </template>
          <template #body-employers>
            <employer-info />
          </template>
          <template #body-candidates>
            <candidate-info />
          </template>
          <template #body-whyuse>
            <why-use-agency-info />
          </template>
        </tabbed-card>
      </div>
      <div
        class="flex w-full flex-col gap-4 px-4 lg:container lg:grid lg:grid-cols-2"
      >
        <article class="border border-grey-20 p-4">
          <h2
            class="mb-8 text-2xl font-bold leading-none text-blue-60 md:text-3xl"
          >
            Advertise on Our Directory
          </h2>
          <p class="pb-8 text-grey-70">
            Are you looking to attract a higher volume of clients and
            candidates? Agency Central is a business development tool that can
            support your agency by providing leads and attracting new talent.
            We've created a hub where employers can find and contact recruitment
            agencies directly, allowing you to manage the recruiting process and
            develop working relationships with clients. Combine this with the
            5,000 candidate contacts we receive daily and Agency Central can
            become a valuable online resource that will aid the continued growth
            of your business. Since establishing our company in 2000, we have
            received contact from more than half a million employers and over 11
            million candidates, making us one of the leading methods of finding
            recruitment agencies in the UK.
          </p>
          <input-button :block="true" style-type="primary-o" href="/agencyarea/index.html"
            >Get Listed</input-button
          >
        </article>
        <article class="border border-grey-20 p-4">
          <div class="flex w-full justify-center">
            <img
              :src="asset_path('/images/deerfoot_logo.png')"
              alt="Deerfoot Logo"
              class="block h-[200px]"
            />
          </div>
          <p class="text-grey-70">
            Since 1997, UK employers have been able to rely on Deerfoot IT to
            supply market leading candidates for roles with current and emerging
            technologies. With no ‘sales culture’ and a focus on research led
            solutions, we believe that we differ from your standard recruiter
            and use this to attract the most in-demand IT professionals in the
            market. Supported by expert consultants that enjoy what they do,
            we’re proud to deliver a dedicated talent acquisition service that
            has propelled us to the forefront of the IT recruitment industry.
          </p>
        </article>
      </div>
    </div>
  </layout-page>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AcCard from 'Components/AcCard.vue';
import NavBar from 'Components/NavBar/NavBar';
import SearchBar from 'Components/SearchBar';
import PopularLinks from 'Components/Homepage/PopularLinks';
import LayoutPage from 'Components/layouts/LayoutPage';
import AcAccordion from 'Components/AcAccordion.vue';
import IconLogoGraphic from 'Components/Icons/IconLogoGraphic';
import InputButton from 'Components/InputButton.vue';
import AboutAgencyCentralInfo from 'Components/Homepage/AboutAgencyCentralInfo';
import EmployerInfo from 'Components/Homepage/EmployerInfo';
import CandidateInfo from 'Components/Homepage/CandidateInfo';
import WhyUseAgencyInfo from 'Components/Homepage/WhyUseAgencyInfo';
import TabbedCard from 'Components/TabbedCard';

export default {
  name: 'IndexPage',
  components: {
    TabbedCard,
    WhyUseAgencyInfo,
    CandidateInfo,
    EmployerInfo,
    AboutAgencyCentralInfo,
    InputButton,
    IconLogoGraphic,
    AcAccordion,
    LayoutPage,
    AcCard,
    SearchBar,
    NavBar,
    PopularLinks,
  },
  data: function () {
    return {
      selectedTabKey: '',
    };
  },
  methods: {
    ...mapActions('info', ['loadInfo']),
    ...mapActions('search', ['resetState']),
    tabInfoChange(key) {
      this.selectedTabKey = key;
    },
  },
  computed: {
    ...mapGetters('industry', ['getSearchbarSkillOptions']),
    ...mapGetters('search', ['currentLocationOption']),
    ...mapState('search', ['parameters']),
    ...mapState('info', ['candidateCount', 'employerCount', 'loaded']),
    ...mapGetters('info', [
      'getPopularLocationLinks',
      'getPopularIndustryLinks',
      'getAllIndustryLinks',
    ]),
  },
  mounted() {
    if (!this.loaded) {
      this.loadInfo();
    }
    // Reset the search state on homepage load
    this.resetState();
  },
};
</script>
